import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft } from 'lucide-react';
import { LEGAL_DOCS, type LegalDocKey } from '@/legal/legalVersions';

import termsMd from '@/legal/content/terms.md?raw';
import privacyMd from '@/legal/content/privacy.md?raw';
import eulaMd from '@/legal/content/eula.md?raw';
import acceptableUseMd from '@/legal/content/acceptable-use.md?raw';

const CONTENT: Record<LegalDocKey, string> = {
  terms: termsMd,
  privacy: privacyMd,
  eula: eulaMd,
  'acceptable-use': acceptableUseMd,
};

/**
 * Public, standalone legal document page — no AppShell, no auth required.
 * Rendered for /legal/terms, /legal/privacy, /legal/eula, /legal/acceptable-use.
 */
export function LegalDocPage({ docKey }: { docKey: LegalDocKey }) {
  const meta = LEGAL_DOCS.find((d) => d.key === docKey);
  const markdown = CONTENT[docKey];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto max-w-3xl px-4 py-4 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" />
            Back
          </Link>
          <span className="text-sm font-semibold">SimCheck.ai</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10">
        <article className="prose prose-sm dark:prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-a:text-primary prose-table:text-foreground">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
        </article>

        <nav className="mt-12 pt-6 border-t border-border flex flex-wrap gap-x-4 gap-y-2 text-sm">
          {LEGAL_DOCS.filter((d) => d.key !== docKey).map((d) => (
            <Link key={d.key} to={d.route} className="text-primary hover:underline">
              {d.title}
            </Link>
          ))}
        </nav>

        {meta && (
          <p className="mt-6 text-xs text-muted-foreground">
            {meta.title} · version {meta.version}
          </p>
        )}
      </main>
    </div>
  );
}

export default LegalDocPage;
